const FEATURE_FLAG_PREFIX = "feature_flag_"

const FEATURE_FLAG_KEYS = {
	AI_DEBUG: "aidebug",
	HEALING_DEBUG_DOWNLOADS: "healingdebugdownloads",
	PERFORMANCE_DEBUG: "performancedebug",
	COLOR_MATCH_DEBUG: "colormatchdebug",
	VIEW_REFERENCES_DEBUG: "viewreferencesdebug",
	PIXIESET_AUTH: "pixiesetauth",
	GENERATIVE_FILL: "generativefill",
	ENABLE_LR_PRESETS: "enablelrpresets",
	MAINTENANCE_BYPASS: "maintenancebypass",
	PEOPLE_MASKING: "peoplemasking",
	RETOUCHING: "retouching",
	RETOUCHING_MASKS_DEBUG: "retouchingmasksdebug",
} as const

const MAINTENANCE_BYPASS_TTL_MS = 2 * 60 * 60 * 1000

const getFeatureFlagKey = (key: string) => {
	return `${FEATURE_FLAG_PREFIX}${key}`
}

const getFeatureFlag = (key: string) => {
	try {
		if (typeof localStorage === "undefined") return false
		return localStorage.getItem(getFeatureFlagKey(key)) === "1"
	} catch {
		return false
	}
}

const setFeatureFlag = (key: string, enabled: boolean) => {
	try {
		if (typeof localStorage === "undefined") return
		if (enabled) {
			localStorage.setItem(getFeatureFlagKey(key), "1")
		} else {
			localStorage.removeItem(getFeatureFlagKey(key))
		}
	} catch {
		// ignore
	}
}

export const isAIDebugEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.AI_DEBUG)
}

export const setAIDebugEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.AI_DEBUG, enabled)
}

export const isPeopleMaskingEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.PEOPLE_MASKING)
}

export const setPeopleMaskingEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.PEOPLE_MASKING, enabled)
}

export const isRetouchingEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.RETOUCHING)
}

export const setRetouchingEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.RETOUCHING, enabled)
}

export const isRetouchingMasksDebugEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.RETOUCHING_MASKS_DEBUG)
}

export const setRetouchingMasksDebugEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.RETOUCHING_MASKS_DEBUG, enabled)
}

export const isHealingDebugDownloadsEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.HEALING_DEBUG_DOWNLOADS)
}

export const setHealingDebugDownloadsEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.HEALING_DEBUG_DOWNLOADS, enabled)
}

export const isPerformanceDebugEnabled = () => {
	if (getFeatureFlag(FEATURE_FLAG_KEYS.PERFORMANCE_DEBUG)) {
		return true
	}

	if (import.meta.env.VITE_PS_DEBUG !== "false") {
		const url = new URL(location.href)
		return url.searchParams.has("debug")
	}

	return false
}

export const setPerformanceDebugEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.PERFORMANCE_DEBUG, enabled)
}

export const isColorMatchDebugEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.COLOR_MATCH_DEBUG)
}

export const setColorMatchDebugEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.COLOR_MATCH_DEBUG, enabled)
}

export const isViewReferencesDebugEnabled = () => {
	return getFeatureFlag(FEATURE_FLAG_KEYS.VIEW_REFERENCES_DEBUG)
}

export const setViewReferencesDebugEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.VIEW_REFERENCES_DEBUG, enabled)
}

export const isPixiesetAuthEnabled = (url?: URL) => {
	const currentUrl = url ?? new URL(location.href)
	const urlParam = getPixiesetAuthInUrl(currentUrl)
	if (urlParam !== null) {
		return urlParam === "1"
	}

	try {
		if (typeof localStorage !== "undefined") {
			const stored = localStorage.getItem(getFeatureFlagKey(FEATURE_FLAG_KEYS.PIXIESET_AUTH))
			if (stored !== null) {
				return stored === "1"
			}
		}
	} catch {
		// ignore
	}

	if (import.meta.env.VITE_BRAND === "pixieset") {
		return true
	}

	return false
}

export const getPixiesetAuthInUrl = (url: URL) => {
	return url.searchParams.get(FEATURE_FLAG_KEYS.PIXIESET_AUTH)
}

export const setPixiesetAuthEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.PIXIESET_AUTH, enabled)
}

export const isGenerativeFillEnabled = (url?: URL) => {
	const currentUrl = url ?? new URL(location.href)
	const urlParam = getGenerativeFillInUrl(currentUrl)
	if (urlParam !== null) {
		return urlParam === "1"
	}

	try {
		if (typeof localStorage !== "undefined") {
			const stored = localStorage.getItem(
				getFeatureFlagKey(FEATURE_FLAG_KEYS.GENERATIVE_FILL),
			)
			if (stored !== null) {
				return stored === "1"
			}
		}
	} catch {
		// ignore
	}

	return true
}

export const getGenerativeFillInUrl = (url: URL) => {
	return url.searchParams.get(FEATURE_FLAG_KEYS.GENERATIVE_FILL)
}

export const setGenerativeFillEnabled = (enabled: boolean) => {
	try {
		if (typeof localStorage === "undefined") return
		localStorage.setItem(
			getFeatureFlagKey(FEATURE_FLAG_KEYS.GENERATIVE_FILL),
			enabled ? "1" : "0",
		)
	} catch {
		// ignore
	}
}

export const getLRPresetsInUrl = (url: URL) => {
	const targetKey = FEATURE_FLAG_KEYS.ENABLE_LR_PRESETS
	for (const [key, value] of url.searchParams.entries()) {
		if (key.toLowerCase() === targetKey) {
			return value
		}
	}
	return null
}

export const isLRPresetsEnabled = (url?: URL) => {
	const currentUrl = url ?? new URL(location.href)
	const urlParam = getLRPresetsInUrl(currentUrl)
	if (urlParam !== null) {
		return urlParam === "1"
	}

	try {
		if (typeof localStorage !== "undefined") {
			const stored = localStorage.getItem(
				getFeatureFlagKey(FEATURE_FLAG_KEYS.ENABLE_LR_PRESETS),
			)
			if (stored !== null) {
				return stored === "1"
			}
		}
	} catch {
		// ignore
	}

	return false
}

export const setLRPresetsEnabled = (enabled: boolean) => {
	setFeatureFlag(FEATURE_FLAG_KEYS.ENABLE_LR_PRESETS, enabled)
}

export const getMaintenanceBypassInUrl = (url: URL) => {
	const targetKey = FEATURE_FLAG_KEYS.MAINTENANCE_BYPASS
	for (const [key, value] of url.searchParams.entries()) {
		if (key.toLowerCase() === targetKey) {
			return value
		}
	}
	return null
}

export const setMaintenanceBypassEnabled = (enabled: boolean) => {
	try {
		if (typeof localStorage === "undefined") return
		const key = getFeatureFlagKey(FEATURE_FLAG_KEYS.MAINTENANCE_BYPASS)
		if (!enabled) {
			localStorage.removeItem(key)
			return
		}

		localStorage.setItem(key, String(Date.now() + MAINTENANCE_BYPASS_TTL_MS))
	} catch {
		// ignore
	}
}

export const isMaintenanceBypassEnabled = () => {
	try {
		if (typeof localStorage === "undefined") return false
		const key = getFeatureFlagKey(FEATURE_FLAG_KEYS.MAINTENANCE_BYPASS)
		const storedExpiresAt = localStorage.getItem(key)
		if (storedExpiresAt === null) {
			return false
		}

		const expiresAt = Number(storedExpiresAt)
		if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
			localStorage.removeItem(key)
			return false
		}

		return true
	} catch {
		return false
	}
}

export const applyMaintenanceBypassFromUrl = (url = new URL(location.href)) => {
	const urlParam = getMaintenanceBypassInUrl(url)
	if (urlParam === null) {
		return false
	}

	if (urlParam === "1") {
		setMaintenanceBypassEnabled(true)
	} else if (urlParam === "0") {
		setMaintenanceBypassEnabled(false)
	}

	for (const key of url.searchParams.keys()) {
		if (key.toLowerCase() === FEATURE_FLAG_KEYS.MAINTENANCE_BYPASS) {
			url.searchParams.delete(key)
			break
		}
	}
	try {
		window.history.replaceState({}, document.title, url)
	} catch {
		// ignore
	}
	return true
}

export const clearAllFeatureFlags = () => {
	try {
		if (typeof localStorage === "undefined") return
		Object.values(FEATURE_FLAG_KEYS).forEach((key) => {
			localStorage.removeItem(getFeatureFlagKey(key))
		})
	} catch {
		// ignore
	}
}
